import React, { useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/ai.css';

const aiData = {
  title: 'AI 서비스',
  overview:
    '제트플로우는 다양한 산업에 적용 가능한 AI 솔루션을 제공합니다. 영상 인식, 데이터 분석, 자동화 등 다양한 분야에서 혁신을 이끌고 있습니다.',
  detailSections: {
    sections: [
      {
        title: '인식 및 체적 계산',
        items: [
          {
            category: '인식',
            description: 'CNN 기반 돼지 객체 인식',
            features: ['CNN을 활용한 폴리곤 좌표 추출 및 다중 객체 감지'],
            images: [
              {
                url: '/images/ai-deeplearning.jpg',
                caption: '돼지 인식',
              },
            ],
          },
          {
            category: '체적 계산',
            description: 'CNN 기반 돼지 객체 인식',
            features: [
              'MiDaS 모델 기반 깊이 추정',
              '단일 RGB 이미지에서 깊이 맵 추정하는 self-supervised 학습 방식',
              '다중 데이터 셋(3D Movies, MegaDepth 등) 사전 학습',
              'PointRend 모델 기반 객체 분할',
              'Iterative subdivision 알고리즘으로 후처리 개선',
              '정밀한 객체 경계 추출',
              'Open3D 라이브러리를 활용한 고품질 포인트 클라우드 생성',
              '데이터 바탕 부피 계산',
            ],
            images: [
              {
                url: '/images/weight.jpg',
                caption: '돼지 인식',
              },
            ],
          },
        ],
      },
      {
        title: '데이터 전처리',
        items: [
          {
            category: '양봉장 현장 데이터',
            description: '양봉장에 설치된 카메라를 통해 해가 들어오는 시간대에 실시간으로 수집된 이미지',
            count: '약 1,500장',
            features: ['실시간 환경 반영', '말벌 출현 및 활동 감지', '다양한 상황 포착', '말벌 감지시 1분간 녹화'],
            images: [
              {
                url: '/images/wasp-data/field-1.jpg',
                caption: '양봉장 현장 데이터 수집 장치',
              },
              {
                url: '/images/wasp-data/field-2.jpg',
                caption: '실시간 말벌 감지 모니터링',
              },
              {
                url: '/images/wasp-data/apiary-black.jpg',
                caption: '실제 현장 수집 데이터',
              },
            ],
          },
          {
            category: '인터넷 수집 데이터',
            description: '인터넷에서 수집한 등검은말벌, 장수말벌, 꿀벌, 사람 등을 포함한 다양한 이미지',
            count: '클래스별 2,000장',
            features: ['균형잡힌 데이터 분포', '다양한 환경 조건', '고품질 이미지'],
            images: [
              {
                url: '/images/wasp-data/internet-1.jpg',
                caption: '등검은말벌 데이터셋',
              },
              {
                url: '/images/wasp-data/internet-2.jpg',
                caption: '장수말벌 데이터셋',
              },
              {
                url: '/images/wasp-data/internet-3.jpg',
                caption: '꿀벌 데이터셋',
              },
            ],
          },
          {
            category: '데이터 증강 기법',
            description: '기하학적 변환',
            features: ['좌우반전 / 회전 / 이동 / 스케일 변경 / 모자이크 / 색도 / 명도 / 채도'],
            images: [
              {
                url: '/images/wasp-data/aug-rotation.jpg',
                caption: '회전 변환 예시',
              },
              {
                url: '/images/wasp-data/aug-flip.jpg',
                caption: '좌우바전 예시',
              },
            ],
          },
          {
            category: '바운딩박스',
            description: 'Segment Anything을 통한 바운딩 박스 처리',
            features: ['Segment Anything 수행', '생성된 이미지 중 적절 이미지 선택', '데이터 전처리 완료'],
            images: [
              {
                url: '/images/wasp-data/segment.jpg',
                caption: 'Segment Anything 처리 화면',
              },
              {
                url: '/images/wasp-data/segment-select.jpg',
                caption: '적절 이미지 선택',
              },
              {
                url: '/images/wasp-data/bounding.jpg',
                caption: '데이터 전처리 결과',
              },
            ],
          },
        ],
      },
      {
        title: '인식 및 추적 알고리즘',
        items: [
          {
            category: 'YOLO 기반 객체 인식',
            description: '실시간 말벌 감지를 위한 고성능 객체 인식 모델',
            features: [
              '다중 객체 동시 감지',
              '실시간 처리 가능한 경량화 모델',
              '높은 정확도의 말벌 분류',
              '인식 프로세스: 학습 데이터 수집 → 학습 데이터 전처리 → YOLO 학습 → 학습 모델 검증 → 인식 수행',
            ],
            images: [
              {
                url: '/images/wasp-data/recognition.jpg',
                caption: '인식 모델 구조',
              },
              {
                url: '/images/wasp-data/recognition-2.jpg',
                caption: '실시간 인식 결과',
              },
            ],
          },
          {
            category: 'BoT-SORT 기반 추적',
            description: 'ID Switching 문제를 해결한 고성능 추적 모델',
            features: ['ID Switching 문제 해결', '다중 객체 동시 추적'],
            images: [
              {
                url: '/images/wasp-data/tracking.jpg',
                caption: '추적 알고리즘 구조',
              },
              {
                url: '/images/wasp-data/tracking-2.jpg',
                caption: '실시간 추적 결과',
              },
            ],
          },
        ],
      },
      {
        title: '예측 알고리즘',
        items: [
          {
            category: 'pose 기반 데이터 전처리',
            description: 'YOLO Pose 모델을 사용하여 머리, 가슴, 복부를 점으로 라벨링',
            features: [
              '말벌의 비행경로 예측을 위한 전처리',
              '각 프레임에서 말벌의 위치를 기록하여 이동 경로 계산',
              '머리와 복부의 위치 차이를 통한 방향 벡터 계산',
              '두 프레임 간 위치 변화로 속도 계산',
            ],
            images: [
              {
                url: '/images/wasp-data/pose.jpg',
                caption: '포즈 모델',
              },
            ],
          },
          {
            category: 'Transformer 기반 비행경로 예측',
            description: '말벌의 비행경로를 예측하여 예상 위치 사격',
            features: [
              '실시간 비행경로 예측',
              '예측 위치를 연속 사격하여 퇴치율 증가',
              '예측 프로세스: 비행경로 데이터 수집 → 학습 데이터 전처리 → Transformer 학습 → 학습 모델 검증 → 예측 수행',
            ],
            images: [
              {
                url: '/images/wasp-data/predict.jpg',
                caption: '예측 알고리즘 구조',
              },
              {
                url: '/images/wasp-data/predict-2.jpg',
                caption: '실시간 예측 결과',
              },
            ],
          },
        ],
      },
    ],
  },
};

const AIPage = () => {
  useScrollAnimation();
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  return (
    <section className="ai-section">
      <div className="ai-container">
        <h3 className="ai-modal-title">{aiData.title}</h3>
        <div className="ai-modal-section">
          {aiData.detailSections.sections.map((section, index) => (
            <div key={index} style={{ marginBottom: '3rem' }}>
              <h4 className="ai-section-title">{section.title}</h4>
              {section.items.map((item, itemIndex) => (
                <div key={itemIndex} className="ai-item-box">
                  {item.category && (
                    <>
                      <h5 className="ai-item-category">{item.category}</h5>
                      <p className="ai-item-description">{item.description}</p>
                      {item.count && <p className="ai-item-count">수량: {item.count}</p>}
                      {item.features && (
                        <ul className="ai-item-features">
                          {item.features.map((feature, featureIndex) => (
                            <li key={featureIndex}>{feature}</li>
                          ))}
                        </ul>
                      )}
                      {item.images && (
                        <div className="ai-image-gallery">
                          {item.images.map((image, imageIndex) => (
                            <div
                              key={imageIndex}
                              className={`ai-gallery-image ${
                                image.url.includes('recognition.jpg') ||
                                image.url.includes('tracking.jpg') ||
                                image.url.includes('predict.jpg')
                                  ? 'wide'
                                  : ''
                              }`}
                              onClick={() => handleImageClick(image.url)}
                            >
                              <img src={image.url} alt={image.caption} />
                              <div className="zoom-icon">
                                <FaSearch size={16} />
                              </div>
                              <div className="image-caption">{image.caption}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {item.technique && (
                    <>
                      <h5 className="ai-item-category">{item.technique}</h5>
                      <p className="ai-item-description">목적: {item.purpose}</p>
                      <ul className="ai-item-features">
                        {item.methods.map((method, methodIndex) => (
                          <li key={methodIndex}>{method}</li>
                        ))}
                      </ul>
                      {item.images && (
                        <div className="ai-image-gallery">
                          {item.images.map((image, imageIndex) => (
                            <div
                              key={imageIndex}
                              className={`ai-gallery-image ${
                                image.url.includes('recognition.jpg') ||
                                image.url.includes('tracking.jpg') ||
                                image.url.includes('predict.jpg')
                                  ? 'wide'
                                  : ''
                              }`}
                              onClick={() => handleImageClick(image.url)}
                            >
                              <img src={image.url} alt={image.caption} />
                              <div className="zoom-icon">
                                <FaSearch size={16} />
                              </div>
                              <div className="image-caption">{image.caption}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {typeof item === 'string' && <p>{item}</p>}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className={`ai-modal-overlay ${selectedImage ? 'open' : 'closed'}`} onClick={handleCloseModal}>
          {selectedImage && (
            <div className="ai-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="ai-close-button" onClick={handleCloseModal}>
                &times;
              </button>
              <img src={selectedImage} alt="확대된 이미지" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AIPage;
